import { AuthType } from '@/types/TAuth';
import moment from 'moment';
import LocalStorageUtility from './localStorage';
import { LocalStorageKeys } from 'src/enums/ELocalStorage';
import { parsePhoneNumber } from 'libphonenumber-js';
import {
	RECEPTIONIST_NUMBER,
	RECEPTIONIST_NUMBER_AU_RESELLER,
	RECEPTIONIST_NUMBER_AUSTRALIA,
	RECEPTIONIST_NUMBER_NL,
	RECEPTIONIST_NUMBER_NL_RESELLER,
	RECEPTIONIST_NUMBER_UK,
	RECEPTIONIST_NUMBER_UK_RESELLER,
	RECEPTIONIST_NUMBER_US_RESELLER,
} from 'src/constants';
import { COUNTRY_CODES } from 'src/enums/ETestingCountry';
import CountryDateFormats from 'src/constants/CountryDateFormat';

export const formatQuestionnaire = (qa?: string) => {
	if (!qa) {
		return [];
	}

	const qaArray = qa
		.trim()
		.split(/(?:Qn\s?\d*|Q\s?\d*|Question\s?\d*|Que\s?\d*|qn\s?\d*|q\s?\d*|due\s?\d*)[\:\-]/i)
		.slice(1)
		.map((qaBlock) => {
			const [question, answer] = qaBlock.split(/(?:^|\n)(?:A|An|Answer|Ans|ans|a)[\:\-]/i);

			return {
				question: question.trim(),
				answer: answer ? answer.trim() : '',
			};
		});

	return qaArray;
};

export const isValidSampleQA = (sampleQA?: string): boolean => {
	if (!sampleQA) {
		return true;
	}

	const questionRegex =
		/(?:Qn\s?\d*|Q\s?\d*|Question\s?\d*|Que\s?\d*|qn\s?\d*|q\s?\d*|due\s?\d*)[\:\-]\s*\S+/i;
	const answerRegex = /(?:^|\n)(?:A|An|Answer|Ans|ans|a)[\:\-]\s*\S+/i;
	return questionRegex.test(sampleQA) && answerRegex.test(sampleQA);
};

export const formatQAString = (qaPairs: { question: string; answer: string }[]) => {
	if (!qaPairs) {
		return '';
	}
	return qaPairs.map(({ question, answer }) => `Q: ${question}\nA: ${answer}`).join('\n\n');
};

export const isResponseValid = (answers: { [key: string]: any }): boolean => {
	if (!answers) {
		return false;
	}
	const answerValues = Object.values(answers);

	if (answerValues.length === 0) {
		return false;
	}
	// Returns true if at least ONE answer is not considered empty.
	return answerValues.some(
		(answer) => answer !== 'N/A' && answer !== null && answer !== undefined && answer !== '',
	);
};

export const renderPSTDate = (utcDate: string, timeZone: string, includeSec?: boolean) => {
	const pstDate = moment.utc(utcDate).tz(timeZone);
	return pstDate.format(`ddd D MMM YY, h:mm${includeSec ? ':ss' : ''} A`);
};

export const deepEqual = (obj1: { [key: string]: any }, obj2: { [key: string]: any }): boolean => {
	if (obj1 === obj2) {
		return true;
	}

	if (typeof obj1 !== 'object' || obj1 === null || typeof obj2 !== 'object' || obj2 === null) {
		return false;
	}

	const keys1 = Object.keys(obj1);
	const keys2 = Object.keys(obj2);

	if (keys1.length !== keys2.length) {
		return false;
	}

	for (const key of keys1) {
		if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) {
			return false;
		}
	}

	return true;
};

export const generateRandomColour = () => {
	const hue = Math.floor(Math.random() * 999);
	const saturation = 50 + Math.random() * 10;
	const lightness = 25 + Math.random() * 10;
	return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

export const parseBooleanField = (field: any, defaultCheck?: boolean): boolean => {
	const isTrueOrFalseString = (value: any) => value === 'true' || value === 'false';

	if (isTrueOrFalseString(field?.S)) {
		return field.S === 'true';
	}
	if (isTrueOrFalseString(field)) {
		return field === 'true';
	}
	if (typeof field?.BOOL === 'boolean') {
		return field.BOOL;
	}
	if (typeof field === 'boolean') {
		return field;
	}

	return defaultCheck === undefined ? false : defaultCheck;
};

// Debounce function to prevent rapid API calls
export const debounce = (func: (...args: any[]) => void, delay: number) => {
	let timer: NodeJS.Timeout;
	return (...args: any[]) => {
		if (timer) {
			clearTimeout(timer);
		}
		timer = setTimeout(() => {
			func(...args);
		}, delay);
	};
};

export const getAuthToken = (userType: AuthType) => {
	switch (userType) {
		case 'RECEPTIONIST':
			// if (window.location.href.includes('reseller_portal') && window.location.href.includes('outbound')) {
			//   return LocalStorageUtility.getLocalData(LocalStorageKeys.AUTH_TOKEN
			// }
			return LocalStorageUtility.getLocalData(LocalStorageKeys.AUTH_TOKEN);
		case 'RESELLER':
			return LocalStorageUtility.getLocalData(LocalStorageKeys.RESELLER_AUTH_TOKEN);
		case 'NOVI':
			if (window.location.href.includes('reseller_portal')) {
				return LocalStorageUtility.getLocalData(LocalStorageKeys.RESELLER_AUTH_TOKEN);
			} else if (window.location.href.includes('campaign/dashboard')) {
				return LocalStorageUtility.getLocalData(LocalStorageKeys.AUTH_TOKEN);
			}
			return LocalStorageUtility.getLocalData(LocalStorageKeys.AUTH_TOKEN);
		case 'NOVI_RECEPTIONIST':
			return LocalStorageUtility.getLocalData(LocalStorageKeys.AUTH_TOKEN);
		case 'ADMIN':
			return LocalStorageUtility.getLocalData(LocalStorageKeys.ADMIN_AUTH_TOKEN);
		default:
			return 'unknown auth type';
	}
};

export const isValidPhone = (phone: string) => {
	// Parse the input number and remove formatting
	const phoneNumber = parsePhoneNumber(phone);

	if (phoneNumber && phoneNumber.isValid()) {
		// Return the standardized E.164 format (e.g., +18011234560)
		return phoneNumber.format('E.164');
	} else {
		return false;
	}
};

export const isValidEmail = (email: string): boolean => {
	return !!String(email)
		.toLowerCase()
		.match(
			/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
		);
};

export const handlePrefixPlusOnPhone = (value: string) => {
	let phone = value;
	if (!phone.startsWith('+')) {
		phone = '+' + phone;
	}
	return phone;
};

export const isEmpty = (val: any): boolean => {
	return (
		val === undefined ||
		val === null ||
		(typeof val === 'object' && Object.keys(val).length === 0) ||
		(typeof val === 'string' && val.trim().length === 0)
	);
};

export const capitalizeFirstLetter = (text: string): string => {
	return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

// Define number mappings
const RECEPTIONIST_NUMBERS = {
	default: {
		standard: RECEPTIONIST_NUMBER,
		reseller: RECEPTIONIST_NUMBER_US_RESELLER,
	},
	[COUNTRY_CODES.US]: {
		standard: RECEPTIONIST_NUMBER,
		reseller: RECEPTIONIST_NUMBER_US_RESELLER,
	},
	[COUNTRY_CODES.AU]: {
		standard: RECEPTIONIST_NUMBER_AUSTRALIA,
		reseller: RECEPTIONIST_NUMBER_AU_RESELLER,
	},
	[COUNTRY_CODES.UK]: {
		standard: RECEPTIONIST_NUMBER_UK,
		reseller: RECEPTIONIST_NUMBER_UK_RESELLER,
	},
	[COUNTRY_CODES.NL]: {
		standard: RECEPTIONIST_NUMBER_NL,
		reseller: RECEPTIONIST_NUMBER_NL_RESELLER,
	},
} as const;

type ResellerFlag = boolean;

/**
 * Gets the appropriate receptionist number based on the country code and reseller status
 * @param phoneNumber - The phone number to check (must include country code)
 * @param isReseller - Whether this is for a reseller account
 * @returns The appropriate receptionist number for the given country and account type
 */
export const getCurrentReceptionistNumber = (
	phoneNumber: string,
	isReseller: ResellerFlag = false,
): string => {
	if (!phoneNumber) {
		return '';
	}
	// Find the matching country code from the phone number
	const countryCode = Object.values(COUNTRY_CODES).find((code) => phoneNumber.startsWith(code));

	// Get the appropriate numbers mapping (default if no country code match)
	const numberMapping = countryCode
		? RECEPTIONIST_NUMBERS[countryCode]
		: RECEPTIONIST_NUMBERS.default;

	// Return the appropriate number based on reseller status
	return numberMapping[isReseller ? 'reseller' : 'standard'];
};

export const getDateFormatFromPhoneNumber = (phoneNumber: string): string => {
	try {
		const phoneNumberParsed = parsePhoneNumber(phoneNumber);
		if (phoneNumberParsed && phoneNumberParsed.country) {
			const countryCode = phoneNumberParsed.country;
			return CountryDateFormats[countryCode] || 'YYYY-MM-DD'; // Default format
		}
		return 'Invalid phone number';
	} catch (error) {
		console.error('Error parsing phone number:', error);
		return 'Invalid phone number';
	}
};

// extract initials from name
export const extractInitials = (name: string) => {
	return name
		?.toUpperCase()
		.split(' ')
		.map((n) => n[0])
		.join('')
		.substring(0, 2);
};

export const checkGptLatency = (
	text: string,
): { latency: boolean; type: 'high' | 'very high' | 'no alert' } => {
	// Count words (handles alphanumeric words and contractions like "it's", "don't")
	const wordCount: number = (text.match(/\b[\w']+\b/g) || []).length;

	// Estimate tokens (1 token ≈ 0.75 words for English)
	const estimatedTokens: number = Math.ceil(wordCount / 0.75);

	// Token Latency with Streaming enable (stream: true)
	// Token Count	  Word Count (approx.)	Latency Estimate	Alert Needed?
	// 0 - 1000	      0 - 750 words	        ~2-5 sec	        ❌ No Alert
	// 1000 - 2000	  750 - 1500 words	    ~5-10 sec	        ⚠️ Soft Warning (if near 2000)
	// 2000 - 3000	  1500 - 2250 words	    ~10-15 sec	      🔴 High Latency (Show Alert)
	// 3000+	        2250+ words	           15+ sec	        🚨 Very High (Consider Trimming)

	if (estimatedTokens > 3000) {
		return {
			latency: true,
			type: 'very high',
		};
	} else if (estimatedTokens > 2000) {
		return {
			latency: true,
			type: 'high',
		};
	} else {
		return {
			latency: false,
			type: 'no alert',
		};
	}
};

export function calculateCredits(pricing: Record<string, number>): Record<string, number> {
	const creditRate = 0.12; // 1 credit = $0.12/min
	const credits: Record<string, number> = {};

	for (const key in pricing) {
		credits[key] = parseFloat((pricing[key] / creditRate).toFixed(1)); // Round to one decimal
	}

	return credits;
}

/**
 * Safely parses JSON with error handling and fallback support
 */
export function safeJSONParse(input: any, fallback: any = null): any {
	if (typeof input !== 'string' || !input) {
		return fallback;
	}

	try {
		return JSON.parse(input);
	} catch (error) {
		console.warn('JSON parsing failed:', error);
		return fallback;
	}
}

/**
 * Copy text to clipboard
 * @param text - The text to copy to clipboard
 */
export const copyToClipboard = (text: string) => {
	navigator.clipboard.writeText(text);
};
